import { Controller } from "react-hook-form";

import { FormDialog } from "ui/form-dialog";
import { Select } from "ui/select";
import { TextField } from "ui/text-field";
import CreativeFormFields from "components/modals/creative-modal/components/creative-form-fields";

import { useAddTouchpoint } from "components/modals/creative-modal/hooks/use-add-touchpoint";
import { TouchpointCreateRequestType } from "generated/schemas";
import {
  getFYOptions,
  getActionTouchpointTypeLabel,
  getActionTouchpointTemplateLabel,
  getActionTouchpointNameLabel,
} from "utilities";

import TouchpointModalIcon from "components/modals/creative-modal/components/touchpoint-modal-icon";
import Icons from "assets";

interface AddTouchpointModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onConfirmClose?: () => void;
  folderId?: string;
  type?: TouchpointCreateRequestType;
}

const AddTouchpointModal = ({
  isOpen,
  onClose,
  onConfirmClose,
  folderId,
  type = TouchpointCreateRequestType.LANDING_PAGE,
}: AddTouchpointModalProps) => {
  const {
    methods,
    isFormValid,
    submitState,
    handleAddTouchpointSubmit,
    handleCancelClick,
    contentTypes,
    contentTypeOptions,
    touchpointTemplateOptions,
    isLoadingTouchpointTemplates,
    classTypeOptions,
    populationTypeOptions,
  } = useAddTouchpoint(isOpen, onClose, onConfirmClose, folderId, type);

  const { control } = methods;

  const fyOptions = getFYOptions();
  const resolvedTitle = `Create ${getActionTouchpointTypeLabel(type).toLowerCase()}`;

  if (!isOpen) return null;

  return (
    <FormDialog
      open
      onClose={handleCancelClick}
      icon={<TouchpointModalIcon type={type} />}
      headline={resolvedTitle}
      form={methods}
      onSubmit={handleAddTouchpointSubmit}
      submitDisabled={!isFormValid}
      submitting={submitState === "loading"}
      submitButtonText={resolvedTitle}
      submitLoadingMessage={`Creating ${getActionTouchpointTypeLabel(type).toLowerCase()}...`}>
      <Controller
        name="touchpointTemplateId"
        control={control}
        render={({ field: { value, onChange, ...field } }) => (
          <Select
            {...field}
            label={getActionTouchpointTemplateLabel(type)}
            selectName="touchpoint-template-select"
            disabled={isLoadingTouchpointTemplates}
            options={touchpointTemplateOptions}
            value={touchpointTemplateOptions?.find((option) => option.value === value) ?? null}
            onChange={(selected) => onChange(selected?.value ?? "")}
            ariaLabel={`Select ${getActionTouchpointTypeLabel(type).toLowerCase()} template`}
            startIcon={<Icons.Search className="text-aqua-600" />}
          />
        )}
      />
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            label={getActionTouchpointNameLabel(type)}
            required
            errMessage={fieldState.error?.message}
            {...field}
          />
        )}
      />
      <CreativeFormFields
        populationTypeOptions={populationTypeOptions}
        contentTypeOptions={contentTypeOptions}
        classTypeOptions={classTypeOptions}
        fyOptions={fyOptions}
        isContentTypeLocked={contentTypes?.length === 1}
      />
    </FormDialog>
  );
};

export default AddTouchpointModal;
